import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Button, Checkbox, Heading, Paragraph, Text, ToggleCard } from '@zillow/constellation-10';
import { logError } from '@zg-rentals/log-error';

import type { RootState } from '../../../../../../store/types';
import { getPlaidLinkToken } from '@renter-hub/actions/requests';
import { DEFAULT_ERROR } from '@renter-hub/constants/notifications';
import { closeNotification, showPageNotification } from '@common/actions/NotificationActions';
import RachelCSEvents from '@renter-hub/tracking/ClickStreamEvents';
import PlaidButton from '@common/components/PlaidButton';
import PaymentMethod from '@common/constants/PaymentMethod';
import mastercardPng from '@assets/mastercard.png';
import visaPng from '@assets/visa.png';
import discoverPng from '@assets/discover.png';
import amexPng from '@assets/amex.png';
import type { RenterPaymentMethodTypeInterface } from '@renter-hub/models/Types';

interface Props {
  isLoading?: boolean;
  modeOfPayment?: string;
  onSelect: (...args: any) => any;
  onContinue: (...args: any) => any;
  onPlaidSubmit: (...args: any) => any;
  paymentMethodTypes: Array<RenterPaymentMethodTypeInterface>;
  fromDashboard: boolean;
}

const PaymentTypeSelection = ({
  modeOfPayment,
  onSelect,
  onContinue,
  onPlaidSubmit,
  paymentMethodTypes,
  isLoading,
  fromDashboard,
}: Props) => {
  const dispatch = useDispatch<any>();
  const [selectedModeOfPayment, setSelectedModeOfPayment] = useState(modeOfPayment);
  const user = useSelector((state: any) => state.user);
  const { plaidEnv, plaidLinkTokenExpiration, plaidLinkToken } = useSelector(
    // @ts-expect-error
    (state) => state.payments.renter.config,
  );
  const propertyId = useSelector((state: RootState) => state.payments.renter.dashboard?.propertyId);

  const updateSelectedModeOfPayment = (selectedMode: string) => {
    setSelectedModeOfPayment(selectedMode);
    onSelect(selectedMode);
  };

  const requestPlaidLinkToken = async () => {
    try {
      await dispatch(closeNotification());
      return await dispatch(getPlaidLinkToken({ propertyId }));
    } catch (err) {
      logError({ error: err });
      return dispatch(showPageNotification(DEFAULT_ERROR));
    }
  };

  return (
    <Box css={{ display: 'flex', flexDirection: 'column', gap: 'token(spacing.md)' }}>
      <Box
        css={{
          display: 'grid',
          gridTemplateColumns: '9fr 3fr',
          alignItems: 'center',
        }}
      >
        <Text textStyle="body-sm">We support these cards and all major banks.</Text>
        <Box
          css={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '8px',
          }}
        >
          <img alt="card-image" src={discoverPng as unknown as string} style={{ height: '15px' }} />
          <img alt="card-image" src={visaPng as unknown as string} style={{ height: '15px' }} />
          <img alt="card-image" src={mastercardPng as unknown as string} style={{ height: '15px' }} />
          <img alt="card-image" src={amexPng as unknown as string} style={{ height: '15px' }} />
        </Box>
      </Box>

      {paymentMethodTypes?.map((paymentMethodType, i) => (
        <ToggleCard.Root
          css={{ padding: 'token(spacing.md) !important' }}
          key={paymentMethodType.name}
          elevated={false}
          selected={selectedModeOfPayment === paymentMethodType.type}
          onClick={() => updateSelectedModeOfPayment(paymentMethodType.type)}
        >
          <ToggleCard.Header css={{ paddingBottom: 'token(spacing.sm) !important' }}>
            <Box css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Heading level={1} textStyle="heading-xs">
                {paymentMethodType.name}
              </Heading>
              <Checkbox checked={selectedModeOfPayment === paymentMethodType.type} />
            </Box>
          </ToggleCard.Header>
          <ToggleCard.Body>
            <Box css={{ display: 'grid', gap: 'token(spacing.md)', marginTop: 'token(spacing.sm) !important' }}>
              <Box
                css={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 'token(spacing.sm)',
                }}
              >
                <Box>
                  <Paragraph>Rent charges</Paragraph>
                  <Paragraph textStyle="body-sm" css={{ color: 'text.subtle' }}>
                    (Rent, move-in payments)
                  </Paragraph>
                </Box>
                <Box>
                  <Paragraph textStyle="body-bold">{paymentMethodType.rentFee}</Paragraph>
                  <Paragraph textStyle="body-sm" css={{ color: 'text.subtle' }}>
                    per transaction
                  </Paragraph>
                </Box>
              </Box>

              <Box
                css={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 'token(spacing.sm)',
                }}
              >
                <Box>
                  <Paragraph>Other rental charges</Paragraph>
                  <Paragraph textStyle="body-sm" css={{ color: 'text.subtle' }}>
                    (Utilities, late fees, etc)
                  </Paragraph>
                </Box>
                <Box>
                  <Paragraph textStyle="body-bold">{paymentMethodType.otherFee}</Paragraph>
                  <Paragraph textStyle="body-sm" css={{ color: 'text.subtle' }}>
                    per transaction
                  </Paragraph>
                </Box>
              </Box>
            </Box>
          </ToggleCard.Body>
        </ToggleCard.Root>
      ))}

      {modeOfPayment === PaymentMethod.TYPE.BANK_ACCOUNT ? (
        <PlaidButton
          plaidEnv={plaidEnv}
          plaidLinkToken={plaidLinkToken}
          plaidLinkTokenExpiration={plaidLinkTokenExpiration}
          getPlaidLinkToken={requestPlaidLinkToken}
          onSuccess={(plaidToken) => onPlaidSubmit({ plaidToken })}
          onExit={() => {}}
          onClickTracking={() => {
            if (!fromDashboard) {
              RachelCSEvents.emitClickContinueFromPaymentSelectionPageEvent({
                user,
                triggerSource: `continue_selected_payment_bank_account`,
              });
            }
          }}
          size="lg"
          label="Continue"
          loading={isLoading}
          useConstellationButton={true}
        />
      ) : (
        <Button emphasis="filled" size="lg" fluid={true} onClick={onContinue} disabled={modeOfPayment == null}>
          Continue
        </Button>
      )}
      <Paragraph css={{ color: 'text.subtle', textAlign: 'center' }}>
        Any payments that are already in progress will continue to process with your previous payment method.
      </Paragraph>
    </Box>
  );
};

export default PaymentTypeSelection;
